<script lang="ts">
  import ModalMessage from "scripts/global_components/child_components/ModalMessage.svelte";
  import InvalidMessage from "scripts/global_components/input/InvalidMessage.svelte";
  import { fetchCustomRequest, fetchGetRequest } from "scripts/utils/fetcher";
  import { numToJPY, priceWithAdditionalFees } from "scripts/utils/price_utils";
  import { pathArr, routeAccounts, routeShop } from "scripts/utils/router";
  import { linebreakToBr } from "scripts/utils/utils";
  import { onMount } from "svelte";
  import ItemChoiceSelect from "./child/ItemChoiceSelect.svelte";
  import FadeCollapse from "scripts/global_components/FadeCollapse.svelte";
  import type {
    Core_Item,
    Core_OrderItemChoice,
    InvalidMessages,
    SelectedValues,
    TypeOrderItem,
  } from "./types";

  const itemCode = pathArr[pathArr.length - 1];
  const endpoint = routeShop.apiItem(itemCode);
  let object: Core_Item;
  let orderItem: TypeOrderItem;
  let selectedValues: SelectedValues;
  let validate;
  let invalidMessages: InvalidMessages;
  let is_valid = true;
  let common_message = "";
  const postEndPoint = routeShop.apiOrderItem();
  let modalDisplay = false;
  let warningDisplay = false;
  let expand = false;
  type DisplayPrice = {
    normal: string;
    discount: string;
  };
  let displayPrice: DisplayPrice = {
    normal: "",
    discount: null,
  }; // 表示される価格
  /** 価格に関する注記 */
  let priceNote = "";
  /** 表示される在庫数 */
  let displayAmount: number[] | number = [];

  const getDataSets = async () => {
    const res = await fetchGetRequest({
      endpoint: endpoint,
      errorMessage: "Itemの取得に失敗しました。",
    });
    if (res.success) object = res.data;
  };

  // orderItem初期化
  const makeOrderItem = () => {
    if (object) {
      let orderItemChoices = [];
      if (selectedValues) {
        Object.entries(selectedValues).forEach(([choiceId, value]) => {
          const itemChoice: Core_OrderItemChoice = {
            id: null,
            order_item: object.pk,
            item_choice: choiceId,
            choice_value: value,
          };
          orderItemChoices.push(itemChoice);
        });
      }
      const orderItem: TypeOrderItem = {
        id: null,
        item: object.pk,
        order: null,
        quantity: 1,
        orderitemchoice_set: orderItemChoices,
      };
      return orderItem;
    }
  };

  /** invalidMessagesを調べて入力値に不正な値がないかを確認 */
  const isSelectedValuesAllClear = (selectedValues: SelectedValues) => {
    let result = true;
    if (selectedValues) {
      invalidMessages = validate(selectedValues);
      const results = Object.values(invalidMessages).map((res) => res.isValid);
      result = !results.includes(false);
    }
    // console.log(result);

    return result;
  };

  const onAddCart = async () => {
    common_message = "";

    if (!isSelectedValuesAllClear(selectedValues)) {
      alert(
        "入力項目に不備があります。入力欄に表示された注意事項（赤字）をご確認いただき、値を入力してからカートに追加するボタンを押してください。",
      );
    } else {
      orderItem = makeOrderItem();
      // console.log("orderItem", orderItem);

      const res = await fetchCustomRequest({
        endpoint: postEndPoint,
        method: "POST",
        fetchObj: orderItem,
      });
      // console.log(res);
      if (res.success) {
        modalDisplay = true;
      } else if (res.error) {
        const errors = res.error;
        is_valid = false;
        if (Array.isArray(errors)) {
          errors.forEach((error) => {
            Object.entries(error).forEach(([id, errors]) => {
              invalidMessages[id] = {
                message: errors[0],
                isValid: false,
              };
            });
          });
        } else {
          if (errors.common) {
            common_message = errors.common[0];
          } else if (errors.detail) {
            common_message = String(errors.detail);
            if (errors.detail == "認証情報が含まれていません。") {
              warningDisplay = true;
            }
          } else {
            Object.entries(errors).forEach(([fieldName, errors]) => {
              if (common_message.length) common_message += ", ";
              common_message += `${fieldName}: ${errors[0]}`;
            });
          }
        }
      }
    }
  };

  /** 項目選択肢が変更された際の処理 */
  const changeSelectedValues = (
    item: Core_Item,
    selectedValues: SelectedValues,
  ) => {
    /** 価格表示 */
    if (item) {
      let prices;
      if (selectedValues)
        prices = priceWithAdditionalFees(item, selectedValues);
      else {
        prices = {
          normal: item.price_include_tax,
          discount: item.discount_price_include_tax
            ? item.discount_price_include_tax
            : null,
        };

        displayAmount = item.free_amount;
      }

      /** 在庫表示 */
      if (isSelectedValuesAllClear(selectedValues)) {
        priceNote = "";
        displayPrice = {
          normal: numToJPY(prices.normal),
          discount: prices.discount ? numToJPY(prices.discount) : null,
        };

        if (selectedValues) {
          let amount_list = Object.entries(selectedValues).map(([id, val]) => {
            const choice = object.itemchoice_set.find(
              (choice) => choice.id == Number(id),
            );
            // console.log("choice", choice, val);

            return choice.branch_type == "i"
              ? val.free_amount
              : item.free_amount;
          });
          amount_list = amount_list.filter((value) => value != null);
          displayAmount = Math.min.apply(null, amount_list);
          // console.log(displayAmount, amount_list);
        }
      } else {
        priceNote = "※ 項目選択肢選択後、正式な金額が表示されます。";
        displayPrice = {
          normal: `${numToJPY(prices.normal)}〜`,
          discount: prices.discount ? `${numToJPY(prices.discount)}〜` : null,
        };
        displayAmount = item.free_amount_range;
      }
    }
  };

  /** fetchがsuccessで終了し、モーダルが開き、カート一覧へのボタンが押されたときの挙動 */
  const gotoCartPage = () => {
    location.href = routeShop.orderSummary();
  };

  const gotoLoginPage = () => {
    location.href = routeAccounts.login;
  };

  // carouselの画像をクリックしたときに、モーダルを開く
  const openImageModal = (event) => {
    const target = event.target;
    const disp_carousel: HTMLElement[] = Array.from(
      document.querySelectorAll(".carousel-thumb"),
    );
    const carouselItems: HTMLElement[] = Array.from(
      document.querySelectorAll(".carousel-item"),
    );
    const car_index = disp_carousel.indexOf(target);
    carouselItems.forEach((el) => {
      el.classList.remove("active");
    });
    carouselItems[car_index].classList.add("active");
    /*const modal = document.getElementById("itemImageModal");
    if (modal) {
      const modalImg = modal.querySelector(".modal-body img");
      if (modalImg) {
        modalImg.src = target.src;
      }
    }*/
  };

  onMount(() => {
    getDataSets();
  });
  //$: console.log(common_message);
  $: if (object) expand = true;
  $: changeSelectedValues(object, selectedValues);
  //$: console.log("selectedValues", selectedValues);
  //$: console.log("item", object);
</script>

<FadeCollapse bind:expand>
  {#if object}
    <div class="row wow fadeIn">
      <!--Grid column-->
      <div class="col-md-6 mb-4">
        <div class="row mb-2">
          <div class="col-12 px-0">
            <button
              class="display-carousel btn-trans"
              data-bs-toggle="modal"
              data-bs-target="#itemImageModal"
              on:click={openImageModal}
            >
              <img
                id="index-{object.itemimage_set[0]?.id}"
                src={object.itemimage_set[0]?.image}
                class="img-fluid carousel-thumb"
                alt=""
              />
            </button>
          </div>
        </div>
        <div class="row wow fadeIn">
          <!--Item Images-->
          {#each object.itemimage_set as img}
            <div class="col-3 col-md-3 m-0 p-0 d-flex align-items-center">
              <button
                class="display-carousel btn-trans"
                data-bs-toggle="modal"
                data-bs-target="#itemImageModal"
              >
                <img
                  id="index-{img.id}"
                  src={img.image}
                  class="img-fluid carousel-thumb"
                  alt=""
                />
              </button>
            </div>
          {/each}
          <!--Item Images-->
        </div>
      </div>
      <!--Grid column-->

      <!--Grid column-->
      <div class="col-md-6 mb-4">
        <!--Content-->
        <div class="p-4">
          <div class="mb-3">
            <span class="text-muted me-1">{object.category}</span>
            {#if object.label}
              <span class="badge bg-{object.label} me-1"
                >{object.get_label_display}</span
              >
            {/if}
          </div>

          <p class="lead font-weight-bold">{object.title} ({object.code})</p>
          <div class="mb-1">
            価格 :
            {#if displayPrice.discount}
              <span class="me-1">
                <del>{displayPrice.normal}</del>
              </span>
              <span class="font-weight-bold text-danger">
                {displayPrice.discount}
              </span>
              <span
                class="mini-text font-weight-bolder text-primary"
                style="opacity:80%;">{priceNote}</span
              >
            {:else}
              <span class="me-1 font-weight-bold">
                {displayPrice.normal}
              </span>
              <span
                class="mini-text font-weight-bolder text-primary"
                style="opacity:80%;">{priceNote}</span
              >
            {/if}
          </div>
          <p class="">
            {#if Array.isArray(displayAmount)}
              {#if displayAmount.length > 1}
                在庫 : <span class="font-weight-bold"
                  >{displayAmount[0]} 〜 {displayAmount[1]}</span
                >
                <span
                  class="mini-text font-weight-bolder text-primary h-6"
                  style="opacity:80%;"
                  >項目選択肢選択後、在庫が表示されます。</span
                >
              {:else}
                在庫 : <span class="font-weight-bold">{displayAmount[0]}</span>
              {/if}
            {:else if displayAmount}
              在庫 : <span class="font-weight-bold">{displayAmount}</span>
            {:else}
              在庫 : <span class="font-weight-bold text-danger">0</span>
            {/if}
          </p>
          <p>{@html linebreakToBr(object.short_description) || ""}</p>
        </div>
        <!--Content-->
      </div>
      <!--Grid column-->
    </div>
    <ItemChoiceSelect
      bind:itemChoices={object.itemchoice_set}
      bind:selectedValues
      bind:validate
      bind:invalidMessages
    />
    <div class="row">
      <div class="col-12">
        <InvalidMessage bind:message={common_message} bind:is_valid />
      </div>
    </div>
    <!--注文ボタン-->
    <div class="row d-flex justify-content-center">
      <div class="col-12 px-1 d-flex justify-content-center">
        {#if Array.isArray(displayAmount)}
          <button
            on:click={onAddCart}
            class="text-center text-nowrap btn btn-primary btn-md m-2 px-3"
          >
            カートに追加<i class="ms-2 mb-1 fas fa-shopping-cart" />
          </button>
        {:else if displayAmount}
          <button
            on:click={onAddCart}
            class="text-center text-nowrap btn btn-primary btn-md m-2 px-3"
          >
            カートに追加<i class="ms-2 mb-1 fas fa-shopping-cart" />
          </button>
        {:else}
          <button
            style="background-color:#78909C;"
            class="text-center text-nowrap btn btn-md m-2 px-3 disabled"
          >
            在庫切れ
          </button>
        {/if}
      </div>
    </div>
    <!--/注文ボタン-->
    <ModalMessage
      bind:open={modalDisplay}
      title="処理完了"
      btnLabel={"カート一覧へ"}
      closeButtonDisplay={true}
      on:buttonClick={gotoCartPage}
    >
      <div class="my-3">
        商品をカートに追加しました。 カートを確認しますか？
      </div>
    </ModalMessage>
    <ModalMessage
      bind:open={warningDisplay}
      title="ユーザー認証情報が確認できません"
      btnLabel={"ログインページへ"}
      closeButtonDisplay={true}
      on:buttonClick={gotoLoginPage}
    >
      <div class="my-3">
        商品購入、カートへの追加はユーザー登録およびログイン後に可能になります。<br
        />
        ログイン手続きを行なってから再度操作を行なってください。
      </div>
    </ModalMessage>
  {/if}
</FadeCollapse>

<style>
  .display-carousel {
    border: none !important;
    background-color: #ffff;
  }
  .mini-text {
    font-size: 0.8rem;
  }
</style>
